import { useIgnitionLazy } from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import {
  ApiExplorerContextProvider,
  useApiExplorerContext,
  useApiExplorerEndpoint,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import {
  generateDefaultInput,
  getDefaultApiExplorerInput,
  lookupPrefill,
  transformInputFromExternalForInternal,
} from '@squareup/dex-utils-shared-api-explorer';
import {
  ROUTER_NOT_READY,
  usePathname,
  useSearchParamsObject,
} from '@squareup/dex-utils-shared-routing-hooks';
import { useRouter } from 'next/router';
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { inputReducer } from './reducers/input-reducer';

interface Props {}

const ApiExplorerInputContainer: FC<PropsWithChildren<Props>> = ({
  children,
}) => {
  // TODO: Implement all input methods
  // - https://block.atlassian.net/browse/DPW-754
  // - https://block.atlassian.net/browse/DPW-755
  // - https://block.atlassian.net/browse/DPW-759
  // Input rules
  // 0. If the user typed any input into a field, show that input
  // 1. If there was a tutorial link, load in the tutorial link
  // 2. If there was a shared link, load in the shared link
  // 3. If there is any input in local storage, load up local storage
  // 4. Otherwise, there is no current input

  const { replace } = useRouter();
  const { pathName, isReady } = usePathname();
  const searchParams = useSearchParamsObject();
  const ignition = useIgnitionLazy();

  const currentPathName = isReady ? pathName : ROUTER_NOT_READY;

  const { data: userInfo, isLoading: isUserInfoLoading } =
    getDeveloperApi().useGetUserInfoQuery();

  const contextValue = useApiExplorerContext();
  const endpoint = useApiExplorerEndpoint();

  const [localInput, dispatchInputAction] = useReducer(
    inputReducer(contextValue.endpoint),
    {
      bodyParameters: {},
      queryParameters: {},
      pathParameters: {},
      authParameters: {},
      fileParameters: {},
    }
  );

  useEffect(() => {
    // Set the initial input, which can be fetched from query params or local storage.
    // Dispatching in a useEffect to avoid hydration mismatching on initial render.
    if (endpoint?.id && ignition && !isUserInfoLoading) {
      const decodedParams = searchParams['params']
        ? ignition.ShareApiHandler.tryDecodeFromQueryParams(
            searchParams['params']
          )
        : undefined;
      const prefill = searchParams['prefill']
        ? lookupPrefill(
            searchParams['prefill'],
            endpoint.id,
            userInfo?.merchant
          )
        : undefined;

      if (decodedParams && decodedParams.isValid) {
        dispatchInputAction({
          type: 'set',
          newState: transformInputFromExternalForInternal(
            {
              ...generateDefaultInput(),
              ...decodedParams.params,
            },
            endpoint
          ),
        });

        // Clear the query params
        replace(currentPathName, undefined, { shallow: true });
      } else if (prefill) {
        dispatchInputAction({
          type: 'set',
          newState: transformInputFromExternalForInternal(prefill, endpoint),
        });
      } else {
        dispatchInputAction({
          type: 'set',
          newState: transformInputFromExternalForInternal(
            getDefaultApiExplorerInput(endpoint.id),
            endpoint
          ),
        });
      }
    }
  }, [
    currentPathName,
    endpoint,
    ignition,
    isUserInfoLoading,
    replace,
    searchParams,
    userInfo?.merchant,
  ]);

  const memoizedContext = useMemo(() => {
    // Validate input
    const input = localInput ? localInput : contextValue.input;
    if (!input.bodyParameters) {
      input.bodyParameters = {};
    }

    if (!input.pathParameters) {
      input.pathParameters = {};
    }

    if (!input.queryParameters) {
      input.queryParameters = {};
    }

    if (!input.authParameters) {
      input.authParameters = {};
    }

    if (!input.fileParameters) {
      input.fileParameters = {};
    }

    return {
      ...contextValue,
      input,
      dispatchInputAction,
    };
  }, [contextValue, localInput]);

  return (
    <ApiExplorerContextProvider value={memoizedContext}>
      {children}
    </ApiExplorerContextProvider>
  );
};

export { ApiExplorerInputContainer };
